
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Search, Loader2, Eye, Filter, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GazetteEvents = () => {
    const { token, can } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fake filters (client side for Phase 4)
    const [statusFilter, setStatusFilter] = useState('ALL');

    useEffect(() => {
        fetchEvents();
    }, [token]);

    const fetchEvents = async () => {
        try {
            const res = await axios.get(`${API}/gazette/events`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEvents(res.data);
        } catch (error) {
            console.error('Error fetching events:', error);
            toast.error('No se pudieron cargar los eventos de gaceta');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, newStatus) => {
        try {
            await axios.put(`${API}/gazette/events/${id}/status?status=${newStatus}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Evento actualizado a ${newStatus}`);
            fetchEvents();
        } catch (error) {
            toast.error('Error al actualizar estado');
        }
    };

    const filteredEvents = events.filter(e => {
        if (statusFilter === 'ALL') return true;
        return e.status === statusFilter;
    });

    if (loading) {
        return (
            <>
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
            </>
        );
    }

    return (
        <>
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Vigilancia de Gaceta</h1>
                        <p className="text-muted-foreground">Coincidencias detectadas en publicaciones del IMPI.</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => navigate('/gazette/rules')}>
                            Gestionar Reglas
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex gap-2 pb-4">
                    <Button
                        variant={statusFilter === 'ALL' ? 'secondary' : 'ghost'}
                        onClick={() => setStatusFilter('ALL')}
                    >
                        Todos
                    </Button>
                    <Button
                        variant={statusFilter === 'NEW' ? 'secondary' : 'ghost'}
                        onClick={() => setStatusFilter('NEW')}
                    >
                        Nuevos
                    </Button>
                    <Button
                        variant={statusFilter === 'REVIEWED' ? 'secondary' : 'ghost'}
                        onClick={() => setStatusFilter('REVIEWED')}
                    >
                        Revisados
                    </Button>
                    <Button
                        variant={statusFilter === 'DISCARDED' ? 'secondary' : 'ghost'}
                        onClick={() => setStatusFilter('DISCARDED')}
                    >
                        Descartados
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Resultados ({filteredEvents.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {filteredEvents.length === 0 ? (
                            <div className="text-center py-10 text-gray-500">
                                No hay eventos para mostrar.
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Gravedad</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead>Fecha</TableHead>
                                        <TableHead>Regla / ID</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredEvents.map((event) => (
                                        <TableRow key={event.id}>
                                            <TableCell>
                                                <Badge variant={event.severity === 'HIGH' ? 'destructive' : 'default'}>
                                                    {event.severity}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {event.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {new Date(event.created_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm">
                                                    <div className="font-medium">Rule ID: {event.rule_id.substring(0, 8)}...</div>
                                                    <div className="text-xs text-gray-500">Item: {event.item_id.substring(0, 8)}...</div>
                                                    <div className="text-xs text-gray-400">Score: {event.similarity_score ? (event.similarity_score * 100).toFixed(0) + '%' : 'N/A'}</div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    {event.status !== 'DISCARDED' && (
                                                        <Button size="icon" variant="ghost" title="Descartar" onClick={() => updateStatus(event.id, 'DISCARDED')}>
                                                            <XCircle className="h-4 w-4 text-red-500" />
                                                        </Button>
                                                    )}
                                                    {event.status !== 'REVIEWED' && (
                                                        <Button size="icon" variant="ghost" title="Marcar Revisado" onClick={() => updateStatus(event.id, 'REVIEWED')}>
                                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
};
